/**
 * FeatureDemo — 기능 페이지 데모 미리보기 컴포넌트.
 *
 * 사용자가 시나리오를 선택하고 버튼을 누르면 가짜 로딩(1.5~2s) 후
 * 미리 생성된 실제 결과물을 표시한다. 각 기능별로 전용 렌더러를 사용.
 */
import { useState, lazy, Suspense, type ReactNode } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { FiPlay, FiChevronDown, FiAlertTriangle, FiCheckCircle, FiFileText, FiMessageSquare, FiGrid } from 'react-icons/fi'

// FTA/ETA 흐름도는 React Flow(무거움) 의존 → lazy 로 분리 로드
const FTAFlowDiagram = lazy(() => import('./FeatureFlowDiagram').then((m) => ({ default: m.FTAFlowDiagram })))
const ETAFlowDiagram = lazy(() => import('./FeatureFlowDiagram').then((m) => ({ default: m.ETAFlowDiagram })))

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────
interface DemoScenario {
  id: string
  label: string
  description: string
  dataKey: string // key in demo data file
}

interface FeatureDemoProps {
  slug: string
  scenarios: DemoScenario[]
  renderResult: (data: unknown, scenarioId: string) => ReactNode
  loadTime?: number // ms
}

// ─────────────────────────────────────────────────────────────
// Loading animation
// ─────────────────────────────────────────────────────────────
function LoadingDots() {
  return (
    <div className="flex items-center gap-1.5">
      {[0, 1, 2].map((i) => (
        <motion.div
          key={i}
          className="w-2 h-2 rounded-full bg-amber-400"
          animate={{ opacity: [0.3, 1, 0.3], scale: [0.8, 1.1, 0.8] }}
          transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.2 }}
        />
      ))}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────
// Main FeatureDemo component
// ─────────────────────────────────────────────────────────────
export function FeatureDemo({ slug, scenarios, renderResult, loadTime = 1800 }: FeatureDemoProps) {
  const [selected, setSelected] = useState(scenarios[0]?.id ?? '')
  const [isOpen, setIsOpen] = useState(false)
  const [phase, setPhase] = useState<'idle' | 'loading' | 'done'>('idle')
  const [demoData, setDemoData] = useState<unknown>(null)

  const selectedScenario = scenarios.find((s) => s.id === selected) ?? scenarios[0]

  const handleRun = async () => {
    if (phase === 'loading') return
    setPhase('loading')
    setDemoData(null)

    try {
      const res = await fetch(`/demo-data/${slug}.json`)
      const data = await res.json()
      await new Promise((r) => setTimeout(r, loadTime))
      setDemoData(data)
      setPhase('done')
    } catch {
      setPhase('idle')
    }
  }

  return (
    <div className="rounded-xl border border-white/[0.10] bg-zinc-900/60 overflow-hidden">
      {/* header bar */}
      <div className="flex items-center gap-3 px-5 py-3.5 border-b border-white/[0.08] bg-zinc-900">
        <div className="flex gap-1.5">
          <div className="w-3 h-3 rounded-full bg-zinc-600" />
          <div className="w-3 h-3 rounded-full bg-zinc-600" />
          <div className="w-3 h-3 rounded-full bg-zinc-600" />
        </div>
        <span className="text-xs text-zinc-500 font-mono ml-1">Heinrich AI Demo</span>
        <div className="ml-auto flex items-center gap-2">
          {phase === 'loading' && <LoadingDots />}
          {phase === 'done' && (
            <span className="text-xs text-emerald-400 flex items-center gap-1">
              <FiCheckCircle size={11} /> 생성 완료
            </span>
          )}
        </div>
      </div>

      {/* scenario selector */}
      <div className="px-5 py-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 border-b border-white/[0.06]">
        <span className="text-xs text-zinc-500 shrink-0">시나리오 선택</span>
        <div className="relative flex-1 min-w-0">
          <button
            onClick={() => setIsOpen((v) => !v)}
            className="w-full flex items-center justify-between gap-2 px-3.5 py-2 rounded-lg bg-zinc-800 border border-white/[0.08] text-sm text-zinc-200 hover:border-white/[0.18] transition"
          >
            <span className="truncate">{selectedScenario?.label ?? '선택'}</span>
            <FiChevronDown size={14} className={`text-zinc-400 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </button>
          <AnimatePresence>
            {isOpen && (
              <motion.div
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.15 }}
                className="absolute z-20 top-full mt-1 left-0 right-0 rounded-lg bg-zinc-800 border border-white/[0.12] shadow-2xl overflow-hidden"
              >
                {scenarios.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => { setSelected(s.id); setIsOpen(false); setPhase('idle'); setDemoData(null) }}
                    className={`w-full text-left px-3.5 py-2.5 text-sm hover:bg-white/[0.06] transition ${
                      s.id === selected ? 'text-amber-400 bg-amber-500/10' : 'text-zinc-200'
                    }`}
                  >
                    <div className="font-medium">{s.label}</div>
                    <div className="text-xs text-zinc-500 mt-0.5">{s.description}</div>
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <button
          onClick={handleRun}
          disabled={phase === 'loading'}
          className={`shrink-0 flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition ${
            phase === 'loading'
              ? 'bg-zinc-700 text-zinc-500 cursor-not-allowed'
              : 'bg-amber-500 hover:bg-amber-400 text-white'
          }`}
        >
          <FiPlay size={13} />
          {phase === 'loading' ? 'AI 분석 중...' : 'AI 분석 시작'}
        </button>
      </div>

      {/* result area */}
      <div className="min-h-[200px]">
        <AnimatePresence mode="wait">
          {phase === 'idle' && (
            <motion.div
              key="idle"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center justify-center gap-3 py-14 text-zinc-600"
            >
              <FiPlay size={28} />
              <p className="text-sm">시나리오를 선택하고 AI 분석을 시작하세요</p>
            </motion.div>
          )}
          {phase === 'loading' && (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center justify-center gap-4 py-14"
            >
              <LoadingDots />
              <p className="text-sm text-zinc-500">AI가 분석 중입니다...</p>
            </motion.div>
          )}
          {phase === 'done' && demoData !== null && (
            <motion.div
              key="done"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              {renderResult(demoData, selected) as ReactNode}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────
// Feature-specific renderers
// ─────────────────────────────────────────────────────────────

// JSA renderer
interface JSARow {
  step: string
  hazard: string
  safeguards: string
  measures?: string
  risk_level_value?: number
  risk_level_before?: string
  risk_level_after?: string
  frequency_before?: number
  severity_before?: number
  frequency_after?: number
  severity_after?: number
  pic?: string
  similar_accidents?: { id: string; summary: string; accident_type_name: string; cause_name: string }[]
}

export function JSADemoResult({ data }: { data: { header?: Record<string, string>; rows?: JSARow[] } }) {
  const rows = data.rows ?? []
  const riskColor = (level: number) =>
    level >= 12 ? 'bg-red-500/20 text-red-400 border-red-500/30'
    : level >= 6 ? 'bg-amber-500/20 text-amber-400 border-amber-500/30'
    : 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'

  return (
    <div className="p-4 space-y-4 max-h-[680px] overflow-y-auto">
      {data.header && (
        <div className="rounded-lg bg-zinc-800/80 border border-white/[0.08] p-3">
          <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-xs">
            <div><span className="text-zinc-500">작업명 </span><span className="text-zinc-200 font-medium">{data.header.work_name}</span></div>
            <div><span className="text-zinc-500">부서 </span><span className="text-zinc-200">{data.header.work_department}</span></div>
            <div><span className="text-zinc-500">일자 </span><span className="text-zinc-200">{data.header.work_date}</span></div>
            <div><span className="text-zinc-500">감독자 </span><span className="text-zinc-200">{data.header.supervisor}</span></div>
            <div className="col-span-2"><span className="text-zinc-500">작업자 </span><span className="text-zinc-200">{data.header.workers}</span></div>
          </div>
        </div>
      )}
      <div className="space-y-3">
        {rows.map((row, i) => (
          <div key={i} className="rounded-xl bg-zinc-800/60 border border-white/[0.08] overflow-hidden">
            {/* Step header */}
            <div className="flex items-center justify-between px-4 py-2.5 bg-zinc-900/60 border-b border-white/[0.06]">
              <span className="text-xs font-mono font-bold text-amber-400">STEP {String(i + 1).padStart(2, '0')}</span>
              <div className="flex items-center gap-2">
                {row.risk_level_before && (
                  <span className={`text-[10px] px-2 py-0.5 rounded border font-semibold ${riskColor(row.risk_level_value ?? 0)}`}>
                    개선 전 {row.risk_level_before}
                  </span>
                )}
                {row.risk_level_after && (
                  <span className="text-[10px] px-2 py-0.5 rounded border border-emerald-500/30 bg-emerald-500/10 text-emerald-400 font-semibold">
                    개선 후 {row.risk_level_after}
                  </span>
                )}
              </div>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-sm font-semibold text-zinc-100">{row.step}</p>
              {/* Risk matrix */}
              {row.frequency_before != null && (
                <div className="flex gap-4 text-xs text-zinc-500">
                  <span>발생빈도 {row.frequency_before}→{row.frequency_after} · 중증도 {row.severity_before}→{row.severity_after}</span>
                  {row.pic && <span className="ml-auto text-zinc-600">담당: {row.pic}</span>}
                </div>
              )}
              {/* Hazard */}
              <div>
                <div className="text-[10px] text-red-400 font-semibold uppercase tracking-wide mb-1">유해·위험요인</div>
                <p className="text-xs text-zinc-300 leading-relaxed">{row.hazard}</p>
              </div>
              {/* Safeguards */}
              <div>
                <div className="text-[10px] text-amber-400 font-semibold uppercase tracking-wide mb-1">현재 안전조치</div>
                <div className="space-y-0.5">
                  {row.safeguards?.split('\n').filter(Boolean).map((line, j) => (
                    <p key={j} className="text-xs text-zinc-400 leading-relaxed">{line}</p>
                  ))}
                </div>
              </div>
              {/* Additional measures */}
              {row.measures && (
                <div>
                  <div className="text-[10px] text-emerald-400 font-semibold uppercase tracking-wide mb-1">추가 개선 조치</div>
                  <div className="space-y-0.5">
                    {row.measures.split('\n').filter(Boolean).map((line, j) => (
                      <p key={j} className="text-xs text-zinc-300 leading-relaxed">{line}</p>
                    ))}
                  </div>
                </div>
              )}
              {/* Similar accidents */}
              {row.similar_accidents && row.similar_accidents.length > 0 && (
                <div>
                  <div className="text-[10px] text-zinc-500 font-semibold uppercase tracking-wide mb-1.5">유사 사고 사례</div>
                  <div className="space-y-1.5">
                    {row.similar_accidents.slice(0, 2).map((acc) => (
                      <div key={acc.id} className="rounded-lg bg-red-500/8 border border-red-500/15 px-3 py-2">
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-400">{acc.accident_type_name}</span>
                          <span className="text-[10px] text-zinc-600">#{acc.id}</span>
                        </div>
                        <p className="text-[11px] text-zinc-400 leading-snug">{acc.summary}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      {/* Excel export preview */}
      <div className="rounded-xl overflow-hidden border border-white/[0.08]">
        <div className="px-4 py-2 bg-zinc-900/60 border-b border-white/[0.06] flex items-center gap-2">
          <FiFileText size={12} className="text-emerald-400" />
          <span className="text-xs text-zinc-400">Excel 출력 미리보기 (실제 다운로드 가능)</span>
        </div>
        <img src="/assets/results/jsa-excel.png" alt="JSA Excel 출력" className="w-full object-cover" />
      </div>
    </div>
  )
}

// TBM renderer
interface TBMData {
  work_description?: string
  date?: string
  location?: string
  weather?: string
  briefing?: {
    summary?: string
    key_hazards?: { rank: number; hazard: string; action: string; law: string }[]
    safety_equipment?: string[]
    emergency_procedure?: string
    similar_cases?: { case_id: string; description: string; severity: string }[]
    laws?: { law: string; content: string }[]
  }
}

export function TBMDemoResult({ data }: { data: TBMData }) {
  const hazards = data.briefing?.key_hazards ?? []
  const rankColor = (rank: number) =>
    rank === 1 ? 'bg-red-500/20 text-red-400 border-red-500/30'
    : rank === 2 ? 'bg-orange-500/20 text-orange-400 border-orange-500/30'
    : rank === 3 ? 'bg-amber-500/20 text-amber-400 border-amber-500/30'
    : 'bg-zinc-600/40 text-zinc-400 border-zinc-600/40'

  return (
    <div className="p-4 space-y-4 max-h-[680px] overflow-y-auto">
      {/* 작업 기본정보 */}
      <div className="rounded-lg bg-zinc-800/80 border border-white/[0.08] p-3">
        <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-xs">
          <div className="col-span-2"><span className="text-zinc-500">작업 </span><span className="text-zinc-200 font-medium">{data.work_description}</span></div>
          <div><span className="text-zinc-500">일자 </span><span className="text-zinc-200">{data.date}</span></div>
          <div><span className="text-zinc-500">날씨 </span><span className="text-zinc-200">{data.weather}</span></div>
          <div className="col-span-2"><span className="text-zinc-500">위치 </span><span className="text-zinc-200">{data.location}</span></div>
        </div>
      </div>

      {/* 오늘의 핵심 요약 */}
      <div className="rounded-lg bg-amber-500/10 border border-amber-500/20 p-3.5">
        <div className="text-xs text-amber-400 font-semibold mb-1.5">AI 오늘의 안전 핵심</div>
        <p className="text-sm text-amber-100 leading-relaxed">{data.briefing?.summary}</p>
      </div>

      {/* 위험요인 전체 */}
      <div>
        <div className="text-xs text-zinc-500 font-semibold mb-2">위험요인 Top {hazards.length}개</div>
        <div className="space-y-2.5">
          {hazards.map((h) => (
            <div key={h.rank} className={`rounded-xl border p-3.5 ${h.rank === 1 ? 'bg-red-500/8 border-red-500/25' : h.rank === 2 ? 'bg-orange-500/8 border-orange-500/20' : 'bg-zinc-800/60 border-white/[0.07]'}`}>
              <div className="flex items-start gap-2.5 mb-2">
                <span className={`mt-0.5 shrink-0 text-xs font-bold px-2 py-0.5 rounded border ${rankColor(h.rank)}`}>
                  위험 {h.rank}순위
                </span>
                <p className="text-sm font-semibold text-zinc-100">{h.hazard}</p>
              </div>
              <div className="pl-0.5 space-y-1.5">
                <p className="text-xs text-emerald-300 leading-relaxed"><span className="text-emerald-500 font-semibold">✓ 조치: </span>{h.action}</p>
                <p className="text-[11px] text-zinc-500">{h.law}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 보호구 */}
      {data.briefing?.safety_equipment && (
        <div>
          <div className="text-xs text-zinc-500 font-semibold mb-2">착용 보호구</div>
          <div className="flex flex-wrap gap-1.5">
            {data.briefing.safety_equipment.map((eq) => (
              <span key={eq} className="text-xs px-2.5 py-1 rounded-full bg-zinc-700 border border-white/[0.06] text-zinc-300">{eq}</span>
            ))}
          </div>
        </div>
      )}

      {/* 비상조치 */}
      {data.briefing?.emergency_procedure && (
        <div className="rounded-lg bg-red-500/8 border border-red-500/20 p-3.5">
          <div className="text-xs text-red-400 font-semibold mb-1.5">비상조치 절차</div>
          <p className="text-xs text-zinc-300 leading-relaxed">{data.briefing.emergency_procedure}</p>
        </div>
      )}

      {/* 유사 사고 사례 */}
      {data.briefing?.similar_cases && data.briefing.similar_cases.length > 0 && (
        <div>
          <div className="text-xs text-zinc-500 font-semibold mb-2">유사 사고 사례 (KOSHA DB)</div>
          <div className="space-y-2">
            {data.briefing.similar_cases.map((c) => (
              <div key={c.case_id} className="rounded-lg bg-zinc-800/60 border border-white/[0.06] p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${c.severity === '사망' ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'}`}>{c.severity}</span>
                  <span className="text-[10px] text-zinc-600">{c.case_id}</span>
                </div>
                <p className="text-xs text-zinc-400 leading-snug">{c.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 관련 법령 */}
      {data.briefing?.laws && data.briefing.laws.length > 0 && (
        <div>
          <div className="text-xs text-zinc-500 font-semibold mb-2">관련 법령</div>
          <div className="space-y-2">
            {data.briefing.laws.map((l, i) => (
              <div key={i} className="rounded-lg bg-blue-500/8 border border-blue-500/15 p-3">
                <div className="text-xs text-blue-400 font-semibold mb-1">{l.law}</div>
                <p className="text-xs text-zinc-400 leading-snug">{l.content}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AI 생성 이미지: 안전 포스터 + 4컷 만화 */}
      <div className="space-y-3">
        <div className="rounded-xl overflow-hidden border border-amber-500/20">
          <div className="px-4 py-2 bg-zinc-900/60 border-b border-amber-500/10 flex items-center gap-2">
            <span className="text-xs text-amber-400 font-semibold">AI 생성 안전 포스터</span>
            <span className="text-[10px] text-zinc-500">현장 부착·출력 가능</span>
          </div>
          <img src="/assets/results/tbm-poster.png" alt="AI 안전 포스터" className="w-full object-contain" />
        </div>
        <div className="rounded-xl overflow-hidden border border-amber-500/20">
          <div className="px-4 py-2 bg-zinc-900/60 border-b border-amber-500/10 flex items-center gap-2">
            <span className="text-xs text-amber-400 font-semibold">AI 생성 4컷 안전 만화</span>
            <span className="text-[10px] text-zinc-500">근로자 공감 교육 콘텐츠</span>
          </div>
          <img src="/assets/results/tbm-comic.png" alt="AI 4컷 안전 만화" className="w-full object-contain" />
        </div>
        <div className="rounded-xl overflow-hidden border border-white/[0.08]">
          <div className="px-4 py-2 bg-zinc-900/60 border-b border-white/[0.06] flex items-center gap-2">
            <FiFileText size={12} className="text-zinc-400" />
            <span className="text-xs text-zinc-400">TBM 양식 출력 미리보기 (PDF 다운로드 가능)</span>
          </div>
          <img src="/assets/results/tbm-result.png" alt="TBM 양식 출력" className="w-full object-contain bg-white" />
        </div>
      </div>
    </div>
  )
}

// Risk Assessment renderer
interface RARow {
  step: string
  hazard: string
  measures: string
  frequency: number
  severity: number
  risk_score: number
  risk_level: string
  frequency_after: number
  severity_after: number
  risk_score_after: number
  risk_level_after: string
  pic?: string
}
interface RAData {
  rows?: RARow[]
  matrix_size?: string
  session_id?: string
  generated_at?: string
}

function RiskMatrixViz({ rows, size }: { rows: RARow[]; size: string }) {
  const parts = size.split('x').map(Number)
  const freqMax = parts[0] ?? 5
  const sevMax = parts[1] ?? freqMax

  // 셀별 위험요인 개수 집계
  const cellCount: Record<string, number> = {}
  for (const row of rows) {
    const key = `${row.frequency}-${row.severity}`
    cellCount[key] = (cellCount[key] ?? 0) + 1
  }

  const score = (f: number, s: number) => f * s
  const cellBg = (f: number, s: number) => {
    const sc = score(f, s)
    const max = freqMax * sevMax
    if (sc >= max * 0.5) return 'bg-red-500 text-white'
    if (sc >= max * 0.25) return 'bg-amber-400 text-zinc-900'
    return 'bg-emerald-500 text-white'
  }

  return (
    <div className="overflow-x-auto">
      <div className="text-xs text-zinc-400 mb-2 font-semibold">위험성 매트릭스 (빈도 × 강도) ({size})</div>
      <div className="inline-block">
        <div style={{ display: 'grid', gridTemplateColumns: `40px repeat(${sevMax}, 52px)`, gap: '3px' }}>
          {/* 헤더 행 */}
          <div className="flex items-end justify-end pb-1"><span className="text-[10px] text-zinc-500 rotate-[-90deg] whitespace-nowrap origin-right translate-x-2">빈도↑</span></div>
          {Array.from({ length: sevMax }, (_, si) => (
            <div key={si} className="text-center text-[10px] text-zinc-500 pb-1">강도 {si + 1}</div>
          ))}
          {/* 빈도 × 강도 셀 (빈도 높은 순) */}
          {Array.from({ length: freqMax }, (_, fi) => freqMax - fi).map((freq) => [
            <div key={`label-${freq}`} className="flex items-center justify-end pr-2 text-[10px] text-zinc-500">{freq}</div>,
            ...Array.from({ length: sevMax }, (_, si) => {
              const sev = si + 1
              const key = `${freq}-${sev}`
              const cnt = cellCount[key] ?? 0
              return (
                <div
                  key={`${freq}-${sev}`}
                  className={`relative flex items-center justify-center rounded text-xs font-bold h-[48px] ${cellBg(freq, sev)}`}
                >
                  <span className="text-[13px]">{score(freq, sev)}</span>
                  {cnt > 0 && (
                    <span className="absolute top-0.5 right-0.5 text-[9px] font-bold bg-black/30 rounded-full w-4 h-4 flex items-center justify-center leading-none">{cnt}</span>
                  )}
                </div>
              )
            }),
          ])}
        </div>
        <div className="flex items-center gap-3 mt-2 text-[10px]">
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-500 inline-block" /> 높음 (≥{Math.round(freqMax * sevMax * 0.5)})</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-400 inline-block" /> 중간 (≥{Math.round(freqMax * sevMax * 0.25)})</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-500 inline-block" /> 낮음</span>
        </div>
      </div>
    </div>
  )
}

export function RiskAssessmentDemoResult({ data }: { data: RAData }) {
  const rows = data.rows ?? []
  const size = data.matrix_size ?? '5x5'

  const levelColor = (level: string) => {
    if (!level || level === 'N/A') return 'text-zinc-500 bg-zinc-700/50'
    const l = level.toLowerCase()
    if (l.includes('높') || l.includes('high') || l.includes('허용불가') || l.includes('intol')) return 'text-red-400 bg-red-500/20'
    if (l.includes('중') || l.includes('med') || l.includes('조건부')) return 'text-amber-400 bg-amber-500/20'
    return 'text-emerald-400 bg-emerald-500/20'
  }

  // 등급 통계
  const high = rows.filter(r => r.risk_level?.includes('높') || r.risk_level?.includes('허용불가')).length
  const med = rows.filter(r => r.risk_level?.includes('중') || r.risk_level?.includes('조건부')).length
  const low = rows.length - high - med
  const afterHigh = rows.filter(r => r.risk_level_after?.includes('높') || r.risk_level_after?.includes('허용불가')).length

  return (
    <div className="max-h-[800px] overflow-y-auto">
      {/* 매트릭스 */}
      <div className="p-4 border-b border-white/[0.06]">
        <RiskMatrixViz rows={rows} size={size} />
      </div>

      {/* 통계 바 */}
      <div className="px-4 py-3 flex items-center gap-2 flex-wrap text-xs border-b border-white/[0.06]">
        <span className="font-semibold text-zinc-400">총 {rows.length}개 위험요인</span>
        <span className="px-2 py-0.5 rounded bg-red-500/15 text-red-400">높음 {high}건</span>
        <span className="px-2 py-0.5 rounded bg-amber-500/15 text-amber-400">중간 {med}건</span>
        <span className="px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-400">낮음 {low}건</span>
        <span className="text-zinc-600 mx-1">→ 개선 후</span>
        <span className="px-2 py-0.5 rounded bg-red-500/15 text-red-400">높음 {afterHigh}건</span>
        <span className="px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-400">낮음 {rows.length - afterHigh}건</span>
      </div>

      {/* 위험성평가표 테이블 */}
      <div className="p-2">
        <div className="text-xs text-zinc-500 px-2 py-1.5 font-semibold">위험성평가표 · {size} 매트릭스</div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse min-w-[700px]">
            <thead>
              <tr className="border-b border-white/[0.10] text-zinc-500">
                <th className="py-2 px-2 text-left font-semibold w-6">#</th>
                <th className="py-2 px-2 text-left font-semibold min-w-[100px]">작업단계</th>
                <th className="py-2 px-2 text-left font-semibold min-w-[140px]">위험요인</th>
                <th className="py-2 px-1 text-center font-semibold w-8">빈도</th>
                <th className="py-2 px-1 text-center font-semibold w-8">강도</th>
                <th className="py-2 px-2 text-center font-semibold w-16">등급</th>
                <th className="py-2 px-2 text-left font-semibold min-w-[120px]">개선조치</th>
                <th className="py-2 px-1 text-center font-semibold w-8">빈도↓</th>
                <th className="py-2 px-1 text-center font-semibold w-8">강도↓</th>
                <th className="py-2 px-2 text-center font-semibold w-16">등급↓</th>
                <th className="py-2 px-2 text-left font-semibold w-16">담당자</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr
                  key={i}
                  className={`border-b border-white/[0.04] ${i % 2 === 0 ? 'bg-zinc-900/30' : 'bg-zinc-800/20'} hover:bg-white/[0.03]`}
                >
                  <td className="py-2 px-2 text-zinc-600 font-mono">{i + 1}</td>
                  <td className="py-2 px-2 text-zinc-400">{row.step}</td>
                  <td className="py-2 px-2 text-zinc-200">{row.hazard}</td>
                  <td className="py-2 px-1 text-center text-zinc-300">{row.frequency}</td>
                  <td className="py-2 px-1 text-center text-zinc-300">{row.severity}</td>
                  <td className="py-2 px-2 text-center">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${levelColor(row.risk_level)}`}>
                      {row.risk_level || 'N/A'}
                    </span>
                  </td>
                  <td className="py-2 px-2 text-zinc-400 text-[11px] leading-snug">{row.measures}</td>
                  <td className="py-2 px-1 text-center text-zinc-300">{row.frequency_after || 'N/A'}</td>
                  <td className="py-2 px-1 text-center text-zinc-300">{row.severity_after || 'N/A'}</td>
                  <td className="py-2 px-2 text-center">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${levelColor(row.risk_level_after)}`}>
                      {row.risk_level_after || 'N/A'}
                    </span>
                  </td>
                  <td className="py-2 px-2 text-zinc-500">{row.pic || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

// Chatbot renderer
export function ChatbotDemoResult({ data, scenarioId }: { data: { conversations?: { id: string; label: string; messages: { role: string; content: string; sources?: { type: string; title: string; excerpt: string }[] }[] }[] }; scenarioId: string }) {
  const conv = data.conversations?.find((c) => c.id === scenarioId) ?? data.conversations?.[0]
  if (!conv) return null
  return (
    <div className="p-4 space-y-3 max-h-[480px] overflow-y-auto">
      {conv.messages.map((msg, i) => (
        <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
          <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
            msg.role === 'user'
              ? 'bg-amber-500/20 text-amber-100 rounded-br-none'
              : 'bg-zinc-800 text-zinc-200 rounded-bl-none border border-white/[0.06]'
          }`}>
            <div className="whitespace-pre-line">{msg.content}</div>
            {msg.sources && msg.sources.length > 0 && (
              <div className="mt-2.5 pt-2.5 border-t border-white/[0.08] space-y-1.5">
                {msg.sources.map((src, j) => (
                  <div key={j} className={`text-xs px-2 py-1.5 rounded-lg ${
                    src.type === 'law' ? 'bg-blue-500/10 text-blue-400'
                    : src.type === 'case' ? 'bg-red-500/10 text-red-400'
                    : 'bg-emerald-500/10 text-emerald-400'
                  }`}>
                    <div className="font-semibold">{src.title}</div>
                    <div className="text-zinc-500 mt-0.5">{src.excerpt}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

// Work Permit renderer
interface WorkPermitData {
  permit_type_label?: string
  header?: Record<string, string | null>
  standard_safety_items?: string[]
  form_data?: {
    work_summary?: string
    safety_items_required?: Record<string, boolean>
    primary_hazards?: { hazard: string; consequence: string }[]
    gas_measurements?: { gas_name: string; expected: string; suggested_time: string }[]
    other_requirements?: string
    attachments_needed?: string[]
    supplementary_permits?: string[]
  }
  sources?: {
    similar_accidents?: { id: string; summary: string; accident_type_name: string; cause_name: string; source: string }[]
    related_laws?: { id: string; law_name: string; original_text: string }[]
  }
}

export function WorkPermitDemoResult({ data }: { data: WorkPermitData }) {
  const safetyItems = data.form_data?.safety_items_required ?? {}
  const checkedCount = Object.values(safetyItems).filter(Boolean).length
  const totalCount = Object.keys(safetyItems).length
  const hazards = data.form_data?.primary_hazards ?? []
  const gasMeasurements = data.form_data?.gas_measurements ?? []
  const similarAccidents = data.sources?.similar_accidents ?? []
  const relatedLaws = data.sources?.related_laws ?? []

  return (
    <div className="p-4 max-h-[720px] overflow-y-auto space-y-4">
      {/* 허가서 헤더 */}
      <div className="rounded-lg bg-zinc-800/80 border border-white/[0.08] p-3">
        <div className="flex items-center gap-2 mb-3">
          <span className="px-2.5 py-1 rounded-lg bg-orange-500/15 text-orange-400 text-xs font-bold border border-orange-500/25">
            {data.permit_type_label ?? '작업허가서'}
          </span>
          {data.header?.permit_no && (
            <span className="text-xs text-zinc-500 font-mono">{data.header.permit_no}</span>
          )}
        </div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
          {data.header?.location && <div className="col-span-2"><span className="text-zinc-500">작업위치 </span><span className="text-zinc-200">{data.header.location}</span></div>}
          {data.header?.permit_date && <div><span className="text-zinc-500">허가일 </span><span className="text-zinc-200">{data.header.permit_date}</span></div>}
          {data.header?.work_start_time && <div><span className="text-zinc-500">작업시간 </span><span className="text-zinc-200">{data.header.work_start_time} ~ {data.header.work_end_time}</span></div>}
        </div>
      </div>

      {/* 작업 개요 */}
      {data.form_data?.work_summary && (
        <div>
          <div className="text-xs text-zinc-500 font-semibold mb-1.5">작업 개요</div>
          <p className="text-xs text-zinc-300 leading-relaxed">{data.form_data.work_summary}</p>
        </div>
      )}

      {/* 주요 위험요인 */}
      {hazards.length > 0 && (
        <div>
          <div className="text-xs text-zinc-500 font-semibold mb-2">주요 위험요인 및 결과</div>
          <div className="space-y-2">
            {hazards.map((h, i) => (
              <div key={i} className="rounded-lg bg-red-500/8 border border-red-500/20 p-3">
                <p className="text-xs font-semibold text-red-300">{h.hazard}</p>
                <p className="text-xs text-zinc-400 mt-1">→ {h.consequence}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 가스 측정 기준 */}
      {gasMeasurements.length > 0 && (
        <div>
          <div className="text-xs text-zinc-500 font-semibold mb-2">가스 측정 기준</div>
          <div className="space-y-1.5">
            {gasMeasurements.map((g, i) => (
              <div key={i} className="rounded-lg bg-zinc-800/60 border border-white/[0.06] p-3">
                <div className="flex items-start justify-between gap-2">
                  <span className="text-xs font-semibold text-zinc-200">{g.gas_name}</span>
                  <span className="text-xs text-emerald-400 font-mono">{g.expected}</span>
                </div>
                <p className="text-[11px] text-zinc-500 mt-0.5">{g.suggested_time}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 안전조치 체크리스트 */}
      <div>
        <div className="text-xs text-zinc-500 font-semibold mb-2">안전조치 체크리스트 ({checkedCount}/{totalCount} 확인)</div>
        <div className="grid grid-cols-2 gap-1.5">
          {Object.entries(safetyItems).map(([item, checked]) => (
            <div key={item} className="flex items-start gap-1.5 text-xs">
              <span className={`mt-0.5 shrink-0 font-bold ${checked ? 'text-emerald-400' : 'text-zinc-600'}`}>
                {checked ? '✓' : '○'}
              </span>
              <span className={checked ? 'text-zinc-300' : 'text-zinc-500'}>{item}</span>
            </div>
          ))}
        </div>
      </div>

      {/* 기타 요구사항 */}
      {data.form_data?.other_requirements && (
        <div className="rounded-lg bg-amber-500/8 border border-amber-500/20 p-3">
          <div className="text-xs text-amber-400 font-semibold mb-1">추가 안전요구사항</div>
          <p className="text-xs text-zinc-300 leading-relaxed">{data.form_data.other_requirements}</p>
        </div>
      )}

      {/* 첨부서류 */}
      {data.form_data?.attachments_needed && data.form_data.attachments_needed.length > 0 && (
        <div>
          <div className="text-xs text-zinc-500 font-semibold mb-1.5">필요 첨부서류</div>
          <div className="space-y-1">
            {data.form_data.attachments_needed.map((a, i) => (
              <div key={i} className="flex items-center gap-1.5 text-xs"><span className="text-zinc-600">📎</span><span className="text-zinc-400">{a}</span></div>
            ))}
          </div>
        </div>
      )}

      {/* 유사 사고 사례 */}
      {similarAccidents.length > 0 && (
        <div>
          <div className="text-xs text-zinc-500 font-semibold mb-2">유사 사고 사례 (글로벌 DB)</div>
          <div className="space-y-1.5">
            {similarAccidents.map((acc) => (
              <div key={acc.id} className="rounded-lg bg-red-500/8 border border-red-500/15 p-3">
                <div className="flex items-center gap-1.5 mb-1">
                  {acc.accident_type_name && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-400">{acc.accident_type_name}</span>}
                  <span className="text-[10px] text-zinc-600">{acc.source} #{acc.id}</span>
                </div>
                <p className="text-[11px] text-zinc-400 leading-snug">{acc.summary}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 관련 법령 */}
      {relatedLaws.length > 0 && (
        <div>
          <div className="text-xs text-zinc-500 font-semibold mb-2">관련 법령 (RAG 검색)</div>
          <div className="space-y-1.5">
            {[...new Map(relatedLaws.map((l) => [l.law_name, l])).values()].map((law) => (
              <div key={law.id} className="rounded-lg bg-blue-500/8 border border-blue-500/15 p-3">
                <div className="text-xs text-blue-400 font-semibold mb-1">{law.law_name}</div>
                <p className="text-[11px] text-zinc-500 leading-snug line-clamp-3">{law.original_text}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// Accident Report renderer
interface AccidentReportData {
  sajangjang?: { name?: string; industry?: string; address?: string }
  workers?: { name?: string; gender?: string; employment_type?: string; injury_type?: string; injury_part?: string; died?: boolean }[]
  overview?: { occurred_at?: string; location?: string; description?: string; overview_5w1h?: string }
  classification?: { accident_type_name?: string; confidence?: number; injury_type?: string; injury_part?: string; severity?: string; reason_ko?: string }
  cause?: { direct_cause?: string; underlying_cause?: string; management_cause?: string; cause_summary_list?: string[] }
  prevention?: {
    engineering_measures?: string[]
    administrative_measures?: string[]
    educational_measures?: string[]
    applicable_laws?: { name: string; excerpt: string }[]
  }
}

export function AccidentReportDemoResult({ data }: { data: AccidentReportData }) {
  const worker = data.workers?.[0]
  return (
    <div className="p-4 space-y-4 max-h-[720px] overflow-y-auto">
      {/* 분류 배지 */}
      <div className="flex items-center gap-2 flex-wrap">
        {data.classification?.accident_type_name && (
          <span className="px-2.5 py-1 rounded-lg bg-red-500/15 text-red-400 text-xs font-bold border border-red-500/25">
            재해유형: {data.classification.accident_type_name}
          </span>
        )}
        {data.classification?.severity && (
          <span className="px-2.5 py-1 rounded-lg bg-orange-500/15 text-orange-400 text-xs font-bold border border-orange-500/25">
            {data.classification.severity}
          </span>
        )}
        {data.classification?.confidence != null && (
          <span className="text-xs text-zinc-500">AI 신뢰도 {Math.round(data.classification.confidence * 100)}%</span>
        )}
      </div>

      {/* 사업장 + 재해자 정보 */}
      <div className="rounded-lg bg-zinc-800/80 border border-white/[0.08] p-3">
        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
          {data.sajangjang?.name && <div className="col-span-2"><span className="text-zinc-500">사업장 </span><span className="text-zinc-200 font-medium">{data.sajangjang.name}</span></div>}
          {data.sajangjang?.industry && <div><span className="text-zinc-500">업종 </span><span className="text-zinc-200">{data.sajangjang.industry}</span></div>}
          {data.sajangjang?.address && <div><span className="text-zinc-500">소재지 </span><span className="text-zinc-200">{data.sajangjang.address}</span></div>}
          {worker?.name && <div><span className="text-zinc-500">재해자 </span><span className="text-zinc-200">{worker.name} ({worker.gender} / {worker.employment_type})</span></div>}
          {worker && <div><span className="text-zinc-500">상해부위 </span><span className="text-red-300">{worker.injury_part} ({worker.injury_type})</span></div>}
        </div>
      </div>

      {/* 사고 개요 */}
      <div>
        <div className="text-xs text-zinc-500 font-semibold mb-1.5">사고 개요</div>
        <div className="rounded-lg bg-zinc-800/60 border border-white/[0.06] p-3">
          {data.overview?.occurred_at && <p className="text-[11px] text-zinc-500 mb-1.5">{data.overview.occurred_at?.replace('T', ' ')} · {data.overview.location}</p>}
          <p className="text-sm text-zinc-200 leading-relaxed">{data.overview?.overview_5w1h ?? data.overview?.description}</p>
        </div>
      </div>

      {/* 분류 판단 근거 */}
      {data.classification?.reason_ko && (
        <div className="rounded-lg bg-red-500/8 border border-red-500/20 p-3">
          <div className="text-xs text-red-400 font-semibold mb-1">AI 분류 근거</div>
          <p className="text-xs text-zinc-300 leading-relaxed">{data.classification.reason_ko}</p>
        </div>
      )}

      {/* 원인 분석 */}
      {data.cause && (
        <div>
          <div className="text-xs text-zinc-500 font-semibold mb-2">원인 분석 (3원인론)</div>
          <div className="space-y-2">
            {data.cause.direct_cause && (
              <div className="rounded-lg bg-zinc-800/60 border border-red-500/20 p-3">
                <div className="text-[10px] text-red-400 font-semibold mb-1">직접원인</div>
                <p className="text-xs text-zinc-300 leading-relaxed">{data.cause.direct_cause}</p>
              </div>
            )}
            {data.cause.underlying_cause && (
              <div className="rounded-lg bg-zinc-800/60 border border-amber-500/20 p-3">
                <div className="text-[10px] text-amber-400 font-semibold mb-1">근원원인</div>
                <p className="text-xs text-zinc-300 leading-relaxed">{data.cause.underlying_cause}</p>
              </div>
            )}
            {data.cause.management_cause && (
              <div className="rounded-lg bg-zinc-800/60 border border-blue-500/20 p-3">
                <div className="text-[10px] text-blue-400 font-semibold mb-1">관리원인</div>
                <p className="text-xs text-zinc-300 leading-relaxed">{data.cause.management_cause}</p>
              </div>
            )}
          </div>
          {data.cause.cause_summary_list && data.cause.cause_summary_list.length > 0 && (
            <div className="mt-2 rounded-lg bg-zinc-800/40 border border-white/[0.06] p-3">
              <div className="text-[10px] text-zinc-500 font-semibold mb-1.5">원인 요약</div>
              <ul className="space-y-1">
                {data.cause.cause_summary_list.map((c, i) => (
                  <li key={i} className="text-xs text-zinc-400 flex gap-1.5"><span className="text-zinc-600 shrink-0">•</span>{c}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* 재발방지 대책 */}
      {data.prevention && (
        <div>
          <div className="text-xs text-zinc-500 font-semibold mb-2">재발방지 대책</div>
          <div className="space-y-2">
            {data.prevention.engineering_measures && data.prevention.engineering_measures.length > 0 && (
              <div className="rounded-lg bg-zinc-800/60 border border-emerald-500/20 p-3">
                <div className="text-[10px] text-emerald-400 font-semibold mb-1.5">공학적 대책</div>
                <ul className="space-y-1">
                  {data.prevention.engineering_measures.map((m, i) => (
                    <li key={i} className="text-xs text-zinc-300 flex gap-1.5 leading-relaxed"><span className="text-emerald-500 shrink-0">✓</span>{m}</li>
                  ))}
                </ul>
              </div>
            )}
            {data.prevention.administrative_measures && data.prevention.administrative_measures.length > 0 && (
              <div className="rounded-lg bg-zinc-800/60 border border-blue-500/20 p-3">
                <div className="text-[10px] text-blue-400 font-semibold mb-1.5">관리적 대책</div>
                <ul className="space-y-1">
                  {data.prevention.administrative_measures.map((m, i) => (
                    <li key={i} className="text-xs text-zinc-300 flex gap-1.5 leading-relaxed"><span className="text-blue-500 shrink-0">✓</span>{m}</li>
                  ))}
                </ul>
              </div>
            )}
            {data.prevention.educational_measures && data.prevention.educational_measures.length > 0 && (
              <div className="rounded-lg bg-zinc-800/60 border border-purple-500/20 p-3">
                <div className="text-[10px] text-purple-400 font-semibold mb-1.5">교육적 대책</div>
                <ul className="space-y-1">
                  {data.prevention.educational_measures.map((m, i) => (
                    <li key={i} className="text-xs text-zinc-300 flex gap-1.5 leading-relaxed"><span className="text-purple-500 shrink-0">✓</span>{m}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          {data.prevention.applicable_laws && data.prevention.applicable_laws.length > 0 && (
            <div className="mt-2 space-y-1.5">
              {data.prevention.applicable_laws.map((l, i) => (
                <div key={i} className="rounded-lg bg-blue-500/8 border border-blue-500/15 p-3">
                  <div className="text-xs text-blue-400 font-semibold mb-0.5">{l.name}</div>
                  <p className="text-[11px] text-zinc-500 leading-snug">{l.excerpt}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// Corrective renderer
export function CorrectiveDemoResult({ data }: { data: { items?: { no: number; category: string; violation: string; risk_grade: string; corrective_action: string; deadline: string; responsible: string; status: string }[]; summary?: { total: number; urgent: number; completed: number; pending: number } } }) {
  const items = data.items ?? []
  return (
    <div className="p-4 space-y-2.5 max-h-[480px] overflow-y-auto">
      {data.summary && (
        <div className="flex gap-3 text-xs mb-3">
          <span className="px-2 py-0.5 rounded bg-zinc-700 text-zinc-300">전체 {data.summary.total}건</span>
          <span className="px-2 py-0.5 rounded bg-red-500/15 text-red-400">즉시 {data.summary.urgent}건</span>
          <span className="px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-400">완료 {data.summary.completed}건</span>
        </div>
      )}
      {items.map((item) => (
        <div key={item.no} className="rounded-lg bg-zinc-800/60 border border-white/[0.06] p-3">
          <div className="flex items-start justify-between gap-2 mb-1">
            <div className="flex items-center gap-2">
              <span className={`text-xs px-1.5 py-0.5 rounded font-bold ${
                item.risk_grade === '즉시 시정' ? 'bg-red-500/15 text-red-400' : 'bg-amber-500/15 text-amber-400'
              }`}>{item.risk_grade}</span>
              <span className="text-xs text-zinc-500">{item.category}</span>
            </div>
            <span className={`text-xs px-1.5 py-0.5 rounded ${item.status === '완료' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-zinc-700 text-zinc-400'}`}>
              {item.status}
            </span>
          </div>
          <p className="text-sm text-zinc-200 mb-1">{item.violation}</p>
          <p className="text-xs text-zinc-400 leading-relaxed">✓ {item.corrective_action}</p>
          <p className="text-xs text-zinc-600 mt-1">기한: {item.deadline} · 담당: {item.responsible}</p>
        </div>
      ))}
    </div>
  )
}

// ETA renderer
// ETA (사건수 분석) renderer
interface ETABranch {
  id: string
  consequence: string
  severity: string
  frequency: number
  risk_level: string
  recommendations?: string[]
}
interface ETASafeguard {
  id: string
  name: string
  reliability: number
  failure: number
  rationale?: string
  source?: string
}
interface ETAData {
  title?: string
  initiating_event_name?: string
  initiating_frequency?: number
  safeguards?: ETASafeguard[]
  branches?: ETABranch[]
  legal_basis?: string[]
  warnings?: string[]
}

export function ETADemoResult({ data }: { data: ETAData }) {
  const branches = data.branches ?? []
  const safeguards = data.safeguards ?? []

  const severityColor = (s: string) =>
    s === 'catastrophic' || s === 'critical' ? 'text-red-400 bg-red-500/15 border-red-500/30'
    : s === 'high' || s === 'major' ? 'text-orange-400 bg-orange-500/15 border-orange-500/30'
    : s === 'medium' || s === 'moderate' ? 'text-amber-400 bg-amber-500/15 border-amber-500/30'
    : 'text-emerald-400 bg-emerald-500/15 border-emerald-500/30'

  return (
    <div className="p-4 space-y-4 max-h-[760px] overflow-y-auto">
      {/* 사건수 분기 트리 (실제 흐름도) */}
      <div>
        <div className="text-xs text-zinc-500 font-semibold mb-2">사건수 분기 트리 (Event Tree) — KOSHA P-87-2021 표준 도면</div>
        <Suspense fallback={<div className="h-[460px] flex items-center justify-center text-xs text-zinc-600">흐름도 로딩 중…</div>}>
          <ETAFlowDiagram data={data as unknown as Parameters<typeof ETAFlowDiagram>[0]['data']} />
        </Suspense>
      </div>

      {/* 초기사건 */}
      <div className="rounded-xl bg-red-500/10 border border-red-500/25 p-4">
        <div className="text-xs text-red-400 font-semibold mb-1">초기사건 (Initiating Event)</div>
        <p className="text-sm font-semibold text-zinc-100">{data.initiating_event_name ?? data.title}</p>
        <p className="text-xs text-zinc-500 mt-1">연간 발생빈도: <span className="text-zinc-300 font-mono">{data.initiating_frequency}/년</span></p>
      </div>

      {/* 안전계층 */}
      <div>
        <div className="text-xs text-zinc-500 font-semibold mb-2">안전 계층 (Safeguards) {safeguards.length}개</div>
        <div className="space-y-2">
          {safeguards.map((sg, i) => (
            <div key={sg.id} className="rounded-lg bg-zinc-800/60 border border-white/[0.08] p-3">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-mono text-amber-400">{sg.id}</span>
                <span className="text-sm font-semibold text-zinc-100">{sg.name}</span>
                <span className="ml-auto text-xs text-zinc-500">신뢰도 {(sg.reliability * 100).toFixed(0)}% / 실패율 {(sg.failure * 100).toFixed(0)}%</span>
              </div>
              {sg.rationale && <p className="text-xs text-zinc-500 leading-snug">{sg.rationale}</p>}
              {sg.source && <p className="text-[10px] text-blue-400 mt-0.5">출처: {sg.source}</p>}
              {/* 안전계층 순서 표시 */}
              <div className="mt-1.5 flex items-center gap-1 text-[10px] text-zinc-600">
                <span className="px-1.5 py-0.5 rounded bg-zinc-700">계층 {i + 1}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 분기 시나리오 */}
      <div>
        <div className="text-xs text-zinc-500 font-semibold mb-2">분기 시나리오 (Event Tree Branches) {branches.length}개</div>
        <div className="space-y-2">
          {branches.map((b, i) => (
            <div key={b.id} className={`rounded-xl border p-3 ${severityColor(b.severity).includes('red') ? 'bg-red-500/5 border-red-500/25' : severityColor(b.severity).includes('amber') ? 'bg-amber-500/5 border-amber-500/20' : 'bg-zinc-800/60 border-white/[0.08]'}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-mono text-zinc-500">{b.id}</span>
                <span className={`text-xs px-2 py-0.5 rounded border font-bold ${severityColor(b.severity)}`}>{b.severity}</span>
                <span className="text-xs text-zinc-400 flex-1">{b.consequence}</span>
                <span className="text-xs font-mono text-zinc-400 shrink-0">{b.frequency.toExponential(2)}/년</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-zinc-500 mb-1.5">
                <span>{b.risk_level}</span>
              </div>
              {b.recommendations && b.recommendations.length > 0 && (
                <ul className="space-y-0.5">
                  {b.recommendations.map((rec, j) => (
                    <li key={j} className="text-xs text-zinc-400 flex gap-1.5"><span className="text-emerald-500 shrink-0">✓</span>{rec}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* 법적 근거 */}
      {data.legal_basis && data.legal_basis.length > 0 && (
        <div className="rounded-lg bg-blue-500/8 border border-blue-500/15 p-3">
          <div className="text-xs text-blue-400 font-semibold mb-1.5">관련 법령 · KOSHA 기준</div>
          <div className="flex flex-wrap gap-1.5">
            {data.legal_basis.map((b, i) => (
              <span key={i} className="text-[10px] px-2 py-0.5 rounded bg-blue-500/10 text-blue-300">{b}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// FTA (결함수 분석) renderer
interface FTANode {
  id: string
  type: 'gate' | 'basic_event'
  name: string
  gate_type?: string
  children?: string[]
  probability?: number | null
}
interface FTAData {
  title?: string
  top_event?: { id: string; name: string }
  nodes?: FTANode[]
  minimal_cut_sets?: string[][]
  top_event_probability?: number
  importance_measures?: Record<string, { fussell_vesely: number; birnbaum: number }>
  legal_basis?: string[]
  warnings?: string[]
}

export function FTADemoResult({ data }: { data: FTAData }) {
  const mcs = data.minimal_cut_sets ?? []
  const nodes = data.nodes ?? []
  const basicEvents = nodes.filter((n) => n.type === 'basic_event')
  const im = data.importance_measures ?? {}
  const imSorted = Object.entries(im).sort(([, a], [, b]) => b.fussell_vesely - a.fussell_vesely)
  const nodeMap = Object.fromEntries(nodes.map((n) => [n.id, n]))

  return (
    <div className="p-4 space-y-4 max-h-[760px] overflow-y-auto">
      {/* 결함수 트리 (실제 흐름도) */}
      <div>
        <div className="text-xs text-zinc-500 font-semibold mb-2">결함수 트리 (Fault Tree) — IEC 61025 / KOSHA P-95-2016 게이트 도면</div>
        <Suspense fallback={<div className="h-[460px] flex items-center justify-center text-xs text-zinc-600">흐름도 로딩 중…</div>}>
          <FTAFlowDiagram data={data as unknown as Parameters<typeof FTAFlowDiagram>[0]['data']} />
        </Suspense>
      </div>

      {/* Top Event */}
      <div className="rounded-xl bg-red-500/10 border border-red-500/30 p-4">
        <div className="text-xs text-red-400 font-semibold mb-1">TOP EVENT (정상사상)</div>
        <p className="text-base font-bold text-zinc-100">{data.top_event?.name ?? data.title}</p>
        {data.top_event_probability != null && (
          <p className="text-xs text-red-300 mt-1 font-mono">
            발생 확률: {data.top_event_probability.toExponential(4)}/년
          </p>
        )}
      </div>

      {/* 기본사상 (Basic Events) */}
      <div>
        <div className="text-xs text-zinc-500 font-semibold mb-2">기본사상 (Basic Events) {basicEvents.length}개</div>
        <div className="space-y-1.5">
          {basicEvents.map((be) => (
            <div key={be.id} className="flex items-center gap-2 rounded-lg bg-zinc-800/60 border border-white/[0.07] px-3 py-2">
              <span className="text-xs font-mono text-amber-400 shrink-0">{be.id}</span>
              <span className="text-xs text-zinc-200 flex-1">{be.name}</span>
              {be.probability != null && (
                <span className="text-xs text-zinc-500 font-mono">{be.probability.toExponential(2)}</span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* 최소컷셋 (MCS) */}
      <div>
        <div className="text-xs text-zinc-500 font-semibold mb-2">최소 컷셋 (MCS, Minimal Cut Sets) {mcs.length}개 — 사고 발생 필요충분 경로</div>
        <div className="space-y-2">
          {mcs.map((cutset, i) => (
            <div key={i} className={`rounded-lg border px-3 py-2 ${i === 0 ? 'bg-red-500/10 border-red-500/25' : i === 1 ? 'bg-amber-500/8 border-amber-500/20' : 'bg-zinc-800/60 border-white/[0.07]'}`}>
              <div className="flex items-center gap-2 text-xs">
                <span className={`font-bold shrink-0 ${i === 0 ? 'text-red-400' : i === 1 ? 'text-amber-400' : 'text-zinc-500'}`}>
                  MCS-{i + 1} ({cutset.length}차)
                </span>
                <span className="text-zinc-300">{cutset.map((id) => nodeMap[id]?.name ?? id).join(' ∩ ')}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Fussell-Vesely 중요도 */}
      {imSorted.length > 0 && (
        <div>
          <div className="text-xs text-zinc-500 font-semibold mb-2">Fussell-Vesely 중요도 순위</div>
          <div className="space-y-1.5">
            {imSorted.map(([id, measures], i) => {
              const name = nodeMap[id]?.name ?? id
              const fv = measures.fussell_vesely
              return (
                <div key={id} className="flex items-center gap-3 text-xs">
                  <span className="text-amber-400 font-mono shrink-0 w-5">#{i + 1}</span>
                  <span className="text-zinc-300 flex-1 min-w-0 truncate">{name}</span>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <div className="w-20 h-1.5 rounded-full bg-zinc-700 overflow-hidden">
                      <div className="h-full rounded-full bg-amber-500" style={{ width: `${fv * 100}%` }} />
                    </div>
                    <span className="text-zinc-500 w-12 text-right">FV={fv.toFixed(3)}</span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* 법적 근거 */}
      {data.legal_basis && data.legal_basis.length > 0 && (
        <div className="rounded-lg bg-blue-500/8 border border-blue-500/15 p-3">
          <div className="text-xs text-blue-400 font-semibold mb-1.5">관련 법령 · KOSHA 기준</div>
          <div className="flex flex-wrap gap-1.5">
            {data.legal_basis.map((b, i) => (
              <span key={i} className="text-[10px] px-2 py-0.5 rounded bg-blue-500/10 text-blue-300">{b}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// Safety Cost (산업안전보건관리비 계상) renderer
interface SafetyCostItem {
  code: string
  label: string
  recommended_amount: number
  ratio: number
  rationale?: string
  notice_clause?: string
  usage_examples?: string[]
}
interface SafetyCostData {
  construction_type?: string
  construction_label?: string
  total_amount?: number
  base_amount?: number
  applied_rate?: number
  base_fee?: number
  total_calculated?: number
  amount_bracket?: string
  smart_equip_limit_amount?: number
  labor_search_limit_amount?: number
  item_breakdown?: SafetyCostItem[]
  legal_basis?: string[]
  warnings?: string[]
  calculation_notes?: string[]
  notice_id?: string
  notice_effective_from?: string
}

const wonFmt = (n: number) => n.toLocaleString('ko-KR')

export function SafetyCostDemoResult({ data }: { data: SafetyCostData }) {
  const items = data.item_breakdown ?? []
  const totalCalc = data.total_calculated ?? 0
  const itemsTotal = items.reduce((s, i) => s + i.recommended_amount, 0)

  // 사용 한도 (스마트 안전장비 20% · 노·사 발굴 항목 15%)
  const caps = [
    { label: '스마트 안전장비', amt: data.smart_equip_limit_amount ?? 0, ratio: 0.20, code: 'SMART_EQUIP' },
    { label: '노·사 발굴 항목', amt: data.labor_search_limit_amount ?? 0, ratio: 0.15, code: 'LABOR_SEARCH' },
  ]

  return (
    <div className="p-4 space-y-4 max-h-[760px] overflow-y-auto">
      {/* 계상 결과 요약 + 계산식 */}
      <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/25 p-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] text-emerald-400 uppercase tracking-wide font-semibold">계상 결과 — {data.construction_label}</div>
            <p className="text-3xl font-bold text-emerald-300 mt-1">₩{wonFmt(totalCalc)}</p>
            <p className="text-xs text-zinc-400 mt-1">
              대상액 ₩{wonFmt(data.base_amount ?? 0)} × {((data.applied_rate ?? 0) * 100).toFixed(4)}% + 기초액 ₩{wonFmt(data.base_fee ?? 0)}
            </p>
          </div>
          <div className="text-right text-xs text-zinc-500 space-y-0.5">
            <div>총공사금액: <span className="text-zinc-300">₩{wonFmt(data.total_amount ?? 0)}</span></div>
            <div>고시: <span className="text-zinc-300">제{data.notice_id}호</span></div>
            {data.notice_effective_from && <div>시행: <span className="text-zinc-300">{data.notice_effective_from}</span></div>}
          </div>
        </div>

        {/* 경고 */}
        {data.warnings && data.warnings.length > 0 && (
          <div className="mt-3 rounded-lg bg-amber-500/10 border border-amber-500/30 p-2.5 space-y-1">
            {data.warnings.map((w, i) => (
              <div key={i} className="flex items-start gap-1.5 text-[11px] text-amber-300">
                <FiAlertTriangle size={11} className="mt-0.5 shrink-0" /><span>{w}</span>
              </div>
            ))}
          </div>
        )}
        {/* 계산 적용 사실 */}
        {data.calculation_notes && data.calculation_notes.length > 0 && (
          <div className="mt-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-2.5">
            <p className="text-[11px] font-semibold text-emerald-300 mb-1">📐 계산 적용 사실</p>
            {data.calculation_notes.map((n, i) => (
              <div key={i} className="flex items-start gap-1.5 text-[11px] text-emerald-200/90">
                <FiCheckCircle size={11} className="mt-0.5 shrink-0" /><span>{n}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 사용 한도 진행 바 */}
      <div className="rounded-xl bg-zinc-800/40 border border-white/[0.07] p-4 space-y-3">
        <h4 className="text-xs font-bold text-zinc-300">사용 한도</h4>
        {caps.map((cap) => {
          const used = items.find((i) => i.code === cap.code)?.recommended_amount ?? 0
          const pct = cap.amt > 0 ? Math.min(100, (used / cap.amt) * 100) : 0
          return (
            <div key={cap.code}>
              <div className="flex justify-between text-xs text-zinc-400 mb-1">
                <span>{cap.label} (한도 {(cap.ratio * 100).toFixed(0)}%)</span>
                <span>₩{wonFmt(used)} / ₩{wonFmt(cap.amt)}</span>
              </div>
              <div className="h-2 rounded-full bg-zinc-700 overflow-hidden">
                <div className={`h-full transition-all ${pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${pct}%` }} />
              </div>
            </div>
          )
        })}
      </div>

      {/* 항목별 분배 표 (별표 4 9분류) */}
      <div>
        <div className="text-xs text-zinc-500 font-semibold mb-2">항목별 권장 사용 분배 (별표 4 · 9분류)</div>
        <div className="overflow-x-auto rounded-lg border border-white/[0.07]">
          <table className="w-full text-xs min-w-[640px]">
            <thead>
              <tr className="bg-zinc-800/60 text-[10px] uppercase text-zinc-500 border-b border-white/[0.08]">
                <th className="px-3 py-2 text-left font-semibold">항목</th>
                <th className="px-3 py-2 text-right font-semibold">권장 금액</th>
                <th className="px-3 py-2 text-right font-semibold">비율</th>
                <th className="px-3 py-2 text-left font-semibold">근거</th>
                <th className="px-3 py-2 text-left font-semibold">사유</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => (
                <tr key={it.code} className={`border-b border-white/[0.04] ${idx % 2 === 0 ? 'bg-zinc-900/30' : 'bg-zinc-800/20'}`}>
                  <td className="px-3 py-2">
                    <div className="font-semibold text-zinc-100 text-[12px]">{it.label}</div>
                    <div className="text-[10px] text-zinc-600 font-mono">{it.code}</div>
                  </td>
                  <td className="px-3 py-2 text-right text-zinc-100 font-semibold whitespace-nowrap">₩{wonFmt(it.recommended_amount)}</td>
                  <td className="px-3 py-2 text-right text-zinc-400">{(it.ratio * 100).toFixed(1)}%</td>
                  <td className="px-3 py-2 text-[10px] text-zinc-400">{it.notice_clause}</td>
                  <td className="px-3 py-2 text-[10px] text-zinc-500 max-w-[220px]">{it.rationale}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-zinc-800/60 font-bold">
                <td className="px-3 py-2 text-zinc-200">합계</td>
                <td className="px-3 py-2 text-right text-emerald-400 whitespace-nowrap">₩{wonFmt(itemsTotal)}</td>
                <td className="px-3 py-2" colSpan={3}></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* 법령 근거 + 주의사항 */}
      <div className="rounded-xl bg-zinc-800/40 border border-white/[0.07] p-4">
        {data.legal_basis && data.legal_basis.length > 0 && (
          <div className="flex items-start gap-2 mb-3">
            <FiCheckCircle size={14} className="text-emerald-400 mt-0.5 shrink-0" />
            <div>
              <h4 className="text-xs font-bold text-zinc-300 mb-1.5">법령 근거</h4>
              <ul className="text-xs text-zinc-400 space-y-1 list-disc pl-4">
                {data.legal_basis.map((l, i) => <li key={i}>{l}</li>)}
              </ul>
            </div>
          </div>
        )}
        <div className="rounded-lg bg-amber-500/5 border border-amber-500/20 p-3 text-[11px] text-amber-200/90 space-y-1">
          <p>• 노·사 발굴 항목은 총 계상액의 <strong className="text-amber-300">15%</strong> 이내로만 사용 가능</p>
          <p>• 스마트 안전장비는 총 계상액의 <strong className="text-amber-300">20%</strong> 이내로만 사용 가능</p>
          <p>• 공사 종료 후 <strong className="text-amber-300">1년간 사용내역서 보존 의무</strong> (제{data.notice_id}호 §10)</p>
          <p>• 본 명세서는 AI 보조 작성. 발주자/도급인 최종 검증 후 사용하시기 바랍니다.</p>
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────
// Safety Education Showcase — 버튼/로딩 없이 바로 결과물 표시
// FeaturePage에서 safety-education 전용으로 직접 사용
// ─────────────────────────────────────────────────────────────
export function SafetyEduShowcase() {
  const [activeTab, setActiveTab] = useState<'slides' | 'video' | 'quiz'>('slides')
  const TABS = [
    { id: 'slides' as const, label: '📊 교육 슬라이드 (PDF)' },
    { id: 'video'  as const, label: '🎬 교육 영상' },
    { id: 'quiz'   as const, label: '📝 문제집' },
  ]
  return (
    <div className="rounded-xl border border-white/[0.10] bg-zinc-900/60 overflow-hidden">
      {/* 탭 헤더 */}
      <div className="flex border-b border-white/[0.08] bg-zinc-900/80">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-5 py-3.5 text-sm font-semibold transition border-b-2 ${
              activeTab === tab.id
                ? 'border-purple-500 text-purple-300 bg-purple-500/5'
                : 'border-transparent text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.02]'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* 슬라이드 PDF */}
      {activeTab === 'slides' && (
        <div>
          <div className="px-5 py-2.5 bg-zinc-800/40 border-b border-white/[0.06] flex items-center justify-between">
            <div>
              <span className="text-xs font-semibold text-zinc-300">건설업 근로자 정기 안전보건교육</span>
              <span className="text-xs text-zinc-500 ml-2">· 2분기 · AI 자동 생성 PPTX</span>
            </div>
            <a href="/assets/edu/safety-edu-slides.pdf" download className="text-xs px-3 py-1.5 rounded-lg bg-purple-500/15 text-purple-400 border border-purple-500/25 hover:bg-purple-500/25 transition">⬇ PDF 다운로드</a>
          </div>
          <iframe
            src="/assets/edu/safety-edu-slides.pdf"
            className="w-full"
            style={{ height: '760px', border: 'none', background: '#fff' }}
            title="안전교육 슬라이드"
          />
        </div>
      )}

      {/* 교육 영상 */}
      {activeTab === 'video' && (
        <div>
          <div className="px-5 py-2.5 bg-zinc-800/40 border-b border-white/[0.06] flex items-center gap-3">
            <span className="text-xs font-semibold text-zinc-300">AI TTS 나레이션 + 슬라이드 자동 합성 영상</span>
            <span className="text-xs text-zinc-500">약 30분 분량 · 법정 교육 인정</span>
          </div>
          <div className="bg-black" style={{ aspectRatio: '16/9' }}>
            <video
              controls
              className="w-full h-full"
              preload="metadata"
              style={{ display: 'block' }}
            >
              <source src="https://heinrich.dasancms.co.kr/media/safety-edu-video.mp4" type="video/mp4" />
            </video>
          </div>
          <div className="px-5 py-3 bg-zinc-800/40 text-xs text-zinc-500">
            🎙 Callirrhoe TTS 엔진 · 실제 Heinrich 서비스에서 생성된 영상 샘플 — 로그인 후 원하는 업종·유형으로 직접 생성 가능
          </div>
        </div>
      )}

      {/* 문제집 */}
      {activeTab === 'quiz' && (
        <div>
          <div className="px-5 py-2.5 bg-zinc-800/40 border-b border-white/[0.06] flex items-center gap-3">
            <span className="text-xs font-semibold text-zinc-300">AI 자동 생성 이해도 평가 문제집</span>
            <span className="text-xs text-zinc-500">교육 내용 기반 자동 출제</span>
          </div>
          <div className="grid grid-cols-2">
            <div className="border-r border-white/[0.08]">
              <div className="px-4 py-2 border-b border-white/[0.06] flex items-center justify-between bg-zinc-900/60">
                <span className="text-xs font-semibold text-zinc-300">문제지 (Q)</span>
                <a href="/assets/edu/safety-edu-quiz-q.pdf" download className="text-xs text-purple-400 hover:underline">⬇ 다운로드</a>
              </div>
              <iframe src="/assets/edu/safety-edu-quiz-q.pdf" className="w-full" style={{ height: '700px', border: 'none', background: '#fff' }} title="문제지" />
            </div>
            <div>
              <div className="px-4 py-2 border-b border-white/[0.06] flex items-center justify-between bg-zinc-900/60">
                <span className="text-xs font-semibold text-zinc-300">정답지 (A)</span>
                <a href="/assets/edu/safety-edu-quiz-a.pdf" download className="text-xs text-purple-400 hover:underline">⬇ 다운로드</a>
              </div>
              <iframe src="/assets/edu/safety-edu-quiz-a.pdf" className="w-full" style={{ height: '700px', border: 'none', background: '#fff' }} title="정답지" />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Safety Education renderer (FeatureDemo wrapper 내부용 — 직접 노출 시 SafetyEduShowcase 사용)
export function SafetyEduDemoResult({ data }: { data: { title?: string; education_type?: string; legal_hours?: number; slides?: unknown[]; video_info?: { duration_min: number; tts: string }; quiz?: { total: number } } }) {
  const [activeTab, setActiveTab] = useState<'slides' | 'video' | 'quiz'>('slides')

  return (
    <div className="max-h-[800px] overflow-y-auto">
      {/* 헤더 정보 */}
      <div className="px-4 py-3 border-b border-white/[0.06] flex items-center gap-2 flex-wrap">
        {data.education_type && (
          <span className="px-2.5 py-1 rounded-lg bg-purple-500/15 text-purple-400 text-xs font-bold border border-purple-500/25">
            {data.education_type}
          </span>
        )}
        {data.legal_hours && <span className="text-xs text-zinc-500">법정 {data.legal_hours}시간</span>}
        {data.title && <span className="text-xs text-zinc-300 ml-2 font-medium">{data.title}</span>}
        <div className="ml-auto flex items-center gap-3 text-xs text-zinc-500">
          {data.video_info?.duration_min && <span>🎬 {data.video_info.duration_min}분</span>}
          {data.quiz?.total && <span>📝 {data.quiz.total}문항</span>}
        </div>
      </div>

      {/* 탭 */}
      <div className="flex border-b border-white/[0.06]">
        {([
          { id: 'slides', label: '📊 교육 슬라이드 (PDF)' },
          { id: 'video',  label: '🎬 교육 영상' },
          { id: 'quiz',   label: '📝 문제집' },
        ] as const).map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2.5 text-xs font-medium transition border-b-2 ${
              activeTab === tab.id
                ? 'border-purple-500 text-purple-400'
                : 'border-transparent text-zinc-500 hover:text-zinc-300'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* 슬라이드 PDF */}
      {activeTab === 'slides' && (
        <div>
          <div className="px-4 py-2 text-xs text-zinc-500 border-b border-white/[0.04]">
            실제 AI 생성 교육 슬라이드 — 건설업 근로자 정기 안전보건교육 (2분기)
          </div>
          <iframe
            src="/assets/edu/safety-edu-slides.pdf"
            className="w-full"
            style={{ height: '680px', border: 'none', background: '#fff' }}
            title="안전교육 슬라이드"
          />
          <div className="px-4 py-2 flex items-center gap-3">
            <a
              href="/assets/edu/safety-edu-slides.pdf"
              download
              className="text-xs px-3 py-1.5 rounded-lg bg-purple-500/15 text-purple-400 border border-purple-500/25 hover:bg-purple-500/25 transition"
            >
              ⬇ PDF 다운로드
            </a>
            <span className="text-xs text-zinc-600">PPTX 원본 다운로드는 로그인 후 이용 가능</span>
          </div>
        </div>
      )}

      {/* 교육 영상 */}
      {activeTab === 'video' && (
        <div>
          <div className="px-4 py-2 text-xs text-zinc-500 border-b border-white/[0.04]">
            실제 AI 생성 교육 영상 — TTS 나레이션 + 슬라이드 애니메이션 자동 합성
          </div>
          <video
            controls
            className="w-full bg-black"
            style={{ maxHeight: '480px' }}
            preload="metadata"
          >
            <source src="https://heinrich.dasancms.co.kr/media/safety-edu-video.mp4" type="video/mp4" />
            브라우저가 video 태그를 지원하지 않습니다.
          </video>
          <div className="px-4 py-2 text-xs text-zinc-600">
            {data.video_info?.tts && <span>🎙 TTS 엔진: {data.video_info.tts} · </span>}
            로그인 후 원하는 교육 유형으로 직접 생성 가능
          </div>
        </div>
      )}

      {/* 문제집 */}
      {activeTab === 'quiz' && (
        <div className="space-y-0">
          <div className="px-4 py-2 text-xs text-zinc-500 border-b border-white/[0.04]">
            AI 자동 생성 문제집 — 교육 내용 기반 이해도 평가
          </div>
          <div className="grid grid-cols-2 gap-0">
            <div className="border-r border-white/[0.06]">
              <div className="px-3 py-2 bg-zinc-900/60 border-b border-white/[0.04]">
                <span className="text-xs font-semibold text-zinc-300">문제지 (Q)</span>
              </div>
              <iframe
                src="/assets/edu/safety-edu-quiz-q.pdf"
                className="w-full"
                style={{ height: '620px', border: 'none', background: '#fff' }}
                title="문제지"
              />
              <div className="px-3 py-1.5">
                <a href="/assets/edu/safety-edu-quiz-q.pdf" download className="text-xs text-purple-400 hover:underline">⬇ 문제지 다운로드</a>
              </div>
            </div>
            <div>
              <div className="px-3 py-2 bg-zinc-900/60 border-b border-white/[0.04]">
                <span className="text-xs font-semibold text-zinc-300">정답지 (A)</span>
              </div>
              <iframe
                src="/assets/edu/safety-edu-quiz-a.pdf"
                className="w-full"
                style={{ height: '620px', border: 'none', background: '#fff' }}
                title="정답지"
              />
              <div className="px-3 py-1.5">
                <a href="/assets/edu/safety-edu-quiz-a.pdf" download className="text-xs text-purple-400 hover:underline">⬇ 정답지 다운로드</a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Image Analysis renderer
interface ImageAnalysisHazard {
  rank: number
  agent_name: string
  agent_category: string
  risk_grade: number
  risk_grade_reason: string
  description: string
  expected_accident_types: string[]
  expected_injuries: string[]
  prevention_measures: string[]
  related_law: string
  top_cases?: { source: string; accident_type_name: string; text_preview: string }[]
  legal_refs?: { law_name: string; text_preview: string }[]
}
interface ImageAnalysisData {
  scene_info?: { work_area?: string; work_content?: string; scene_description?: string }
  ranking?: ImageAnalysisHazard[]
  stop_work_recommended?: boolean
  stop_work_reason?: string
}

export function ImageAnalysisDemoResult({ data }: { data: ImageAnalysisData }) {
  const ranking = data.ranking ?? []
  const gradeColor = (g: number) =>
    g >= 5 ? 'bg-red-500/20 text-red-400 border-red-500/30'
    : g >= 4 ? 'bg-orange-500/20 text-orange-400 border-orange-500/30'
    : g >= 3 ? 'bg-amber-500/20 text-amber-400 border-amber-500/30'
    : 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'

  return (
    <div className="space-y-0 max-h-[720px] overflow-y-auto">
      {/* 분석 대상 이미지 */}
      <div className="relative">
        <img
          src="/assets/results/image-analysis.png"
          alt="AI 위험분석 대상 이미지"
          className="w-full object-contain max-h-[280px]"
        />
        <div className="absolute top-2 left-2 px-2 py-1 rounded bg-black/70 text-[10px] text-white">
          AI 분석 대상 이미지
        </div>
        {data.stop_work_recommended && (
          <div className="absolute top-2 right-2 px-2.5 py-1.5 rounded-lg bg-red-600/90 border border-red-500 flex items-center gap-1.5">
            <FiAlertTriangle size={13} className="text-white" />
            <span className="text-xs font-bold text-white">STOP WORK</span>
          </div>
        )}
      </div>

      <div className="p-4 space-y-4">
        {/* STOP WORK 상세 */}
        {data.stop_work_recommended && data.stop_work_reason && (
          <div className="rounded-lg bg-red-500/15 border border-red-500/30 p-3.5">
            <p className="text-xs font-bold text-red-400 mb-1">즉시 작업중지 권고 사유</p>
            <p className="text-xs text-red-300 leading-relaxed">{data.stop_work_reason}</p>
          </div>
        )}

        {/* 현장 정보 */}
        {data.scene_info && (
          <div className="rounded-lg bg-zinc-800/80 border border-white/[0.08] p-3">
            <div className="text-xs text-zinc-500 font-semibold mb-2">현장 분석</div>
            <div className="space-y-1 text-xs">
              <div><span className="text-zinc-500">작업구역 </span><span className="text-zinc-200">{data.scene_info.work_area}</span></div>
              <div><span className="text-zinc-500">작업내용 </span><span className="text-zinc-200">{data.scene_info.work_content}</span></div>
              <p className="text-zinc-400 leading-relaxed mt-1.5">{data.scene_info.scene_description}</p>
            </div>
          </div>
        )}

        {/* 위험요인 전체 */}
        <div>
          <div className="text-xs text-zinc-500 font-semibold mb-2">검출 위험요인 Top {ranking.length}개</div>
          <div className="space-y-3">
            {ranking.map((item) => (
              <div key={item.rank} className={`rounded-xl border overflow-hidden ${item.risk_grade >= 5 ? 'border-red-500/30 bg-red-500/5' : item.risk_grade >= 4 ? 'border-orange-500/20 bg-orange-500/5' : 'border-white/[0.08] bg-zinc-800/60'}`}>
                <div className="flex items-center gap-2.5 px-3.5 py-2.5 border-b border-white/[0.06]">
                  <span className={`text-xs px-2 py-0.5 rounded border font-bold ${gradeColor(item.risk_grade)}`}>위험도 {item.risk_grade}/5</span>
                  <span className="text-sm font-semibold text-zinc-100">{item.agent_name}</span>
                  <span className="ml-auto text-[10px] text-zinc-600">{item.agent_category} · #{item.rank}</span>
                </div>
                <div className="p-3.5 space-y-2.5">
                  <p className="text-xs text-zinc-300 leading-relaxed">{item.description}</p>
                  {item.risk_grade_reason && (
                    <p className="text-xs text-amber-400/80 leading-relaxed"><span className="font-semibold">위험 판단 근거: </span>{item.risk_grade_reason}</p>
                  )}
                  {/* 사고유형 + 예상 부상 */}
                  <div className="flex flex-wrap gap-1.5">
                    {item.expected_accident_types?.map((t) => (
                      <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-400">{t}</span>
                    ))}
                    {item.expected_injuries?.map((t) => (
                      <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-orange-500/12 text-orange-400">{t}</span>
                    ))}
                  </div>
                  {/* 예방조치 */}
                  {item.prevention_measures?.length > 0 && (
                    <div>
                      <div className="text-[10px] text-emerald-400 font-semibold mb-1">예방조치</div>
                      <ul className="space-y-0.5">
                        {item.prevention_measures.map((m, j) => (
                          <li key={j} className="text-xs text-zinc-300 flex gap-1.5"><span className="text-emerald-500 shrink-0">✓</span>{m}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {/* 관련 법령 */}
                  {item.legal_refs && item.legal_refs.length > 0 && (
                    <div className="space-y-1">
                      {item.legal_refs.map((ref, j) => (
                        <div key={j} className="rounded bg-blue-500/8 border border-blue-500/15 px-2.5 py-1.5">
                          <div className="text-[10px] text-blue-400 font-semibold">{ref.law_name}</div>
                          <p className="text-[11px] text-zinc-500 mt-0.5">{ref.text_preview}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* 유사 사고 */}
                  {item.top_cases && item.top_cases.length > 0 && (
                    <div className="space-y-1">
                      <div className="text-[10px] text-zinc-500 font-semibold">유사 사고 사례</div>
                      {item.top_cases.map((c, j) => (
                        <div key={j} className="rounded bg-red-500/8 border border-red-500/12 px-2.5 py-1.5">
                          <div className="text-[10px] text-red-400">{c.source} · {c.accident_type_name}</div>
                          <p className="text-[11px] text-zinc-500 mt-0.5">{c.text_preview}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
